import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ImportRow, ImportRowExclusionHandlers } from '../../importRows';
import { RowExclusionLockContext } from '../rowExclusion';
import { ImportCardRows } from './ImportCardRows';

type ImportCardListProps = ImportRowExclusionHandlers & {
    /** The rows to show, in file order. */
    rows: ImportRow[];
    /** Every row of the file, for the footer's "of m". */
    totalRows: number;
    /** What the list says with no row to show: the file has none, or the tab. */
    emptyMessage: string;
    /** A card's press: opens its row in the drawer. */
    onOpenRow: (rowNumber: number) => void;
    /** The import is running: no card opens and no exclusion changes. */
    isDisabled?: boolean;
};

/**
 * The Products table on a phone: the same rows as cards, one under the
 * other, on HeroUI Pro's `ListView` in virtualized mode, with the table's
 * footer under them.
 *
 * - **Virtualized with the page:** the list is as tall as its cards and the
 *   page scrolls it (React Aria's virtualizer follows the window's scroll),
 *   so only the cards on screen are in the DOM and no inner scroll area
 *   fights the page's. Each card is measured as it renders (the height is an
 *   estimate until then) and again when its row changes.
 * - **Re-rendering:** a row the hook kept (`buildRows`) is the same object,
 *   so the list reuses its card; an exclusion re-renders that card alone.
 *   The language and the exclusion handlers are the list's dependencies, so
 *   a switch of language re-renders and re-measures every card.
 * - Pressing a card (a tap, or Enter on the focused card), its Edit or, on
 *   a flagged row, its note's Fix opens its row in the drawer
 *   (`onOpenRow`); its Exclude / Include button is its own. While the
 *   import runs (`isDisabled`) none of them does anything, without a
 *   render of the list (`ImportCardRows`): the card's press reads the state
 *   when it happens, and the buttons read `RowExclusionLockContext`.
 *
 * Memoized, as the table is: a render of the step that leaves these props
 * alone skips the list.
 */
export const ImportCardList = React.memo<ImportCardListProps>(
    function ImportCardList({
        rows,
        totalRows,
        emptyMessage,
        onExcludeRow,
        onIncludeRow,
        onOpenRow,
        isDisabled = false,
    }) {
        const { t } = useTranslation('pages');
        const isDisabledRef = React.useRef(isDisabled);
        isDisabledRef.current = isDisabled;
        const onEditRow = React.useCallback(
            (rowNumber: number) => {
                if (!isDisabledRef.current) {
                    onOpenRow(rowNumber);
                }
            },
            [onOpenRow],
        );
        const onAction = React.useCallback(
            (key: React.Key) => onEditRow(Number(key)),
            [onEditRow],
        );

        return (
            <div
                className="flex flex-col gap-3"
                {...getNodeTestIdProp('catalog-import-products-cards')}
            >
                <RowExclusionLockContext.Provider value={isDisabled}>
                    <ImportCardRows
                        emptyMessage={emptyMessage}
                        onAction={onAction}
                        onEditRow={onEditRow}
                        onExcludeRow={onExcludeRow}
                        onIncludeRow={onIncludeRow}
                        rows={rows}
                    />
                </RowExclusionLockContext.Provider>
                <Typography
                    color="muted"
                    type="body-sm"
                    {...getNodeTestIdProp('catalog-import-products-footer')}
                >
                    {t('catalogImport.products.table.footer', {
                        count: totalRows,
                        shown: countParam(rows.length),
                        total: countParam(totalRows),
                    })}
                </Typography>
            </div>
        );
    },
);
